import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	EventEmitter,
	inject,
	Input,
	OnInit,
	Output,
	signal,
	WritableSignal,
} from '@angular/core';

import { FormControl } from '@angular/forms';
import {
	MatSlideToggleChange,
	MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import { ChecklistOptionsComponent } from '@app/pages/cmms/components/work-order/work-order-checklist/checklist-options/checklist-options.component';
import { AuTableLayoutModule } from '@shared/ui-components';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { ChecklistProgressComponent } from '@app/pages/cmms/components/work-order/work-order-checklist/checklist-progress/checklist-progress.component';
import { ChecklistItemsComponent } from '@app/pages/cmms/components/work-order/work-order-checklist/checklist-items/checklist-items.component';
import { AuDialogService } from '@core/services/au-dialog.service';
import { Observable } from 'rxjs';
import { AuUtilsFunctions } from '@app/shared';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { TranslateService } from '@tolgee/ngx';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { IWorkOrderChecklist } from '@app/domain/cmms-types/workorder-types';
import { IChecklistTemplate } from '@app/domain/types';

@Component({
	selector: 'work-order-checklist',
	imports: [
		MatSlideToggleModule,
		ChecklistOptionsComponent,
		AuTableLayoutModule,
		MatExpansionModule,
		MatIconModule,
		ChecklistProgressComponent,
		ChecklistItemsComponent,
		MatButtonModule,
		NgxTolgeeModule,
	],
	templateUrl: './work-order-checklist.component.html',
	styleUrls: ['./work-order-checklist.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkOrderChecklistComponent implements OnInit {
	private readonly dialogService = inject(AuDialogService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly translateService = inject(TranslateService);
	@Input() set checklists(checklists: IWorkOrderChecklist[]) {
		const template =
			checklists[0]?.template?.length > 0 ? JSON.parse(checklists[0].template) : null;
		this.checklistTemplate.set(template);
		this.editedChecklistTemplate = structuredClone(template);
	}
	@Input() isExistingWorkOrder: boolean = false;
	@Output() controlReady = new EventEmitter<FormControl<string>>();
	@Output() checklistTemplateChange = new EventEmitter<string>();
	@Output() checklistEnabled = new EventEmitter<boolean>();
	@Output() checklistDeleted = new EventEmitter<void>();

	formControl = new FormControl<string>('');
	parsedChecklistFormControl: IChecklistTemplate = null;

	isChecklistEnabled: WritableSignal<boolean> = signal(false);

	editedChecklistTemplate: IChecklistTemplate = null;
	checklistTemplate: WritableSignal<IChecklistTemplate> = signal(null);

	totalOptionsCount = computed(() => this.checklistTemplate().overallCount);
	completedOptionsCount = computed(() => {
		const options = this.checklistTemplate().options;
		return options?.filter(option => option.answer !== null).length;
	});
	isEditMode: WritableSignal<boolean> = signal(false);
	isPanelOpen: WritableSignal<boolean> = signal(true);

	private get editModeDialogData() {
		return {
			title: this.translateService.instant('exit_edit_mode'),
			description: `<p class="au-cmms-alert-text">${this.translateService.instant('unsaved_checklist_changes')}</p><p class="au-cmms-alert-text">${this.translateService.instant('proceed_confirm')}</p>`,
			actionBtnText: this.translateService.instant('yes'),
		};
	}

	private get toggleDialogData() {
		return {
			title: this.translateService.instant('turn_off_checklist'),
			description: `<p class="au-cmms-alert-text">${this.translateService.instant('checklist_data_not_saved')}</p><p class="au-cmms-alert-text">${this.translateService.instant('proceed_confirm')}</p>`,
			actionBtnText: this.translateService.instant('yes'),
		};
	}

	private get deleteDialogData() {
		return {
			title: this.translateService.instant('delete_checklist'),
			description: `<p class="au-cmms-alert-text">${this.translateService.instant('delete_checklist_confirm')}</p><p class="au-cmms-alert-text">${this.translateService.instant('loose_populated_data')}</p>`,
			actionBtnText: this.translateService.instant('delete'),
		};
	}

	ngOnInit() {
		this.controlReady.emit(this.formControl);
		this.checkIfChecklistIsEnabled();
	}

	checkIfChecklistIsEnabled() {
		/**
		 * This stuff is needed to check if checklist is present when we are navigating
		 * back and forth while creating WO, as in case we don't want to have the editMode enabled,
		 * it should be just the value filled and toggle switched.
		 * That data exists only in formValue
		 */
		this.formControl.valueChanges
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				filter(value => !!value)
			)
			.subscribe(value => {
				if (!this.isChecklistEnabled()) {
					this.setChecklistToggleValues(true);
					this.parsedChecklistFormControl = JSON.parse(value);
				}
			});
	}

	toggleChecklist(event: MatSlideToggleChange) {
		if (!event.checked && this.checklistHasValue()) {
			event.source.checked = true;
			this.notifyUserAndDisableChecklist();
			return;
		}

		this.setChecklistToggleValues(event.checked);
	}

	checklistOptionsChanged(evt: string) {
		this.formControl.setValue(evt);
		this.editedChecklistTemplate = evt.length ? JSON.parse(evt) : null;
	}

	cancelChecklistChanges() {
		if (this.checklistHasValue() && this.checklistHasChanges()) {
			this.notifyUserAndDisableChecklist();
		} else {
			this.setChecklistToggleValues(false);
			this.isEditMode.set(false);
		}
	}

	saveChecklistChanges(checklistTemplate: string) {
		this.isEditMode.set(false);
		this.checklistTemplateChange.emit(checklistTemplate);
	}

	deleteChecklist() {
		const dialog = this.dialogService.openAlertDialog(this.deleteDialogData);

		this.dialogService.closedByEnter(dialog).subscribe(approved => {
			if (approved) {
				this.disableChecklist();
				this.checklistDeleted.emit();
			}
		});
	}

	checklistTemplateChanged(template: string) {
		this.checklistTemplateChange.emit(template);
	}

	afterCollapseHandler() {
		this.isPanelOpen.set(false);
	}

	afterExpandHandler() {
		this.isPanelOpen.set(true);
	}

	editChecklist($event: MouseEvent) {
		$event.stopPropagation();
		this.isEditMode.set(true);
	}

	private alertAboutDataLoss(): Observable<boolean> {
		const dialogData = this.isEditMode()
			? this.editModeDialogData
			: this.toggleDialogData;
		const dialog = this.dialogService.openAlertDialog(dialogData);

		return this.dialogService.closedByEnter(dialog);
	}

	private notifyUserAndDisableChecklist(): void {
		this.alertAboutDataLoss().subscribe(approved => {
			if (approved) {
				this.disableChecklist();
			}
		});
	}

	private disableChecklist(): void {
		this.setChecklistToggleValues(false);
		this.formControl.setValue('');
		this.parsedChecklistFormControl = null;
		this.isEditMode.set(false);
		this.editedChecklistTemplate = structuredClone(this.checklistTemplate());
	}

	private setChecklistToggleValues(checked: boolean): void {
		this.isChecklistEnabled.set(checked);
		this.checklistEnabled.emit(checked);
	}

	private checklistHasValue(): boolean {
		return !!this.formControl.value || !!this.checklistTemplate();
	}

	private checklistHasChanges(): boolean {
		return AuUtilsFunctions.isNotEqual(
			this.editedChecklistTemplate,
			this.checklistTemplate()
		);
	}
}
